import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { EmployeeSearchSelect } from "@/components/EmployeeSearchSelect";
import { toast } from "sonner";
import { ArrowLeftRight, Plane, CheckCircle2, StickyNote, Trash2, RefreshCw, MapPin, ClipboardCheck, Eye, Wrench, UserPlus, Home, Archive } from "lucide-react";

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const STATUS_CLS = {
  normal: "bg-emerald-100 text-emerald-800",
  heavy_check: "bg-blue-100 text-blue-800",
  mel: "bg-amber-100 text-amber-800",
  capex: "bg-rose-100 text-rose-800",
  unserviceable: "bg-red-100 text-red-800",
  offline: "bg-gray-200 text-gray-600",
};

const NOTE_STATUS = {
  open: { label: "Open", cls: "bg-gray-200 text-gray-600" },
  acknowledged: { label: "Acknowledged", cls: "bg-blue-100 text-blue-800" },
  in_progress: { label: "In progress", cls: "bg-amber-100 text-amber-800" },
  completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-800" },
};

const PRIORITIES = ["Low", "Medium", "High", "Urgent"];

export default function Handovers() {
  const { user } = useAuth();
  const hasAccess = user?.handover_access || user?.role === "admin";
  const [summary, setSummary] = useState(null);
  const [notes, setNotes] = useState([]);
  const [cities, setCities] = useState([]);
  const [members, setMembers] = useState([]);
  const [noteBase, setNoteBase] = useState("");
  const [noteText, setNoteText] = useState("");
  const [viewBase, setViewBase] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pushJob, setPushJob] = useState(null);
  const [assignTask, setAssignTask] = useState(null);
  const [archive, setArchive] = useState(null);

  const openArchive = async (tail) => {
    setArchive({ tail, loading: true, jobs: [], daily_inspections: [] });
    try {
      const r = await api.get(`/handovers/archive/${encodeURIComponent(tail)}`);
      setArchive({ loading: false, ...r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load archive");
      setArchive(null);
    }
  };

  const load = async () => {
    try {
      const [s, n, c, m] = await Promise.all([
        api.get("/handovers/summary"),
        api.get("/handovers/notes"),
        api.get("/cities"),
        api.get("/maintenance/members"),
      ]);
      setSummary(s.data);
      setNotes(n.data);
      setCities(c.data);
      setMembers(m.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load handovers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (hasAccess) {
      load();
      if (user?.city) setNoteBase(user.city);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addNote = async () => {
    if (!noteBase) { toast.error("Pick a base for the note"); return; }
    if (!noteText.trim()) { toast.error("Write a note first"); return; }
    setBusy(true);
    try {
      await api.post("/handovers/notes", { base: noteBase, text: noteText.trim() });
      toast.success("Handover note added");
      setNoteText("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add note");
    } finally {
      setBusy(false);
    }
  };

  const deleteNote = async (n) => {
    if (!window.confirm("Delete this handover note?")) return;
    try {
      await api.delete(`/handovers/notes/${n.id}`);
      toast.success("Note deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete note");
    }
  };

  const ackNote = async (n) => {
    try {
      await api.post(`/handovers/notes/${n.id}/ack`);
      toast.success("Acknowledged");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to acknowledge");
    }
  };

  const doPushJob = async () => {
    if (!pushJob.tail) { toast.error("Pick an aircraft"); return; }
    setBusy(true);
    try {
      await api.post(`/handovers/notes/${pushJob.note.id}/push-job`, { tail: pushJob.tail, priority: pushJob.priority });
      toast.success(`Open job created on ${pushJob.tail}`);
      setPushJob(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create job");
    } finally {
      setBusy(false);
    }
  };

  const doAssignTask = async () => {
    if (!assignTask.assignee_id) { toast.error("Pick a person"); return; }
    setBusy(true);
    try {
      await api.post(`/handovers/notes/${assignTask.note.id}/assign-task`, { assignee_id: assignTask.assignee_id });
      toast.success("Assigned as a task");
      setAssignTask(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign task");
    } finally {
      setBusy(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="handovers-no-access">
        <ArrowLeftRight className="w-10 h-10 mx-auto text-gray-300" />
        <h1 className="font-display text-xl mt-4">Handovers</h1>
        <p className="text-sm text-gray-500 mt-2">You don&apos;t have access to this page. Ask an admin to grant Handovers access in Admin Settings.</p>
      </div>
    );
  }

  const userBase = (user?.city || "").toUpperCase();
  const allTails = (summary?.bases || []).flatMap((b) => b.aircraft.map((a) => a.tail)).sort();
  const cityOrder = cities.map((c) => c.code);
  // Union of bases that have aircraft or notes — user's base always on top.
  const summaryCodes = (summary?.bases || []).map((b) => b.code);
  const noteOnly = [...new Set(notes.map((n) => n.base))].filter((b) => !summaryCodes.includes(b))
    .sort((a, b) => (cityOrder.indexOf(a) === -1 ? 999 : cityOrder.indexOf(a)) - (cityOrder.indexOf(b) === -1 ? 999 : cityOrder.indexOf(b)));
  let orderedBases = [...summaryCodes.filter((c) => c !== "UNASSIGNED"), ...noteOnly];
  if (orderedBases.includes(userBase)) orderedBases = [userBase, ...orderedBases.filter((b) => b !== userBase)];
  if (summaryCodes.includes("UNASSIGNED")) orderedBases.push("UNASSIGNED");
  if (viewBase) orderedBases = orderedBases.filter((b) => b === viewBase);

  const baseName = (code) => (summary?.bases || []).find((b) => b.code === code)?.name || cities.find((c) => c.code === code)?.name || (code === "UNASSIGNED" ? "No base set" : code);
  const baseAircraft = (code) => (summary?.bases || []).find((b) => b.code === code)?.aircraft || [];

  const renderNote = (n) => {
    const st = NOTE_STATUS[n.status || "open"] || NOTE_STATUS.open;
    const linkable = (n.status || "open") !== "completed" && !n.linked_wo_id && !n.linked_task_id;
    return (
      <div key={n.id} className={`border rounded-lg bg-white px-3 py-2.5 ${(n.status || "open") === "completed" ? "border-emerald-200 bg-emerald-50/40" : "border-summit-hairline"}`} data-testid={`handover-note-${n.id}`}>
        <div className="flex items-center gap-2 flex-wrap">
          {(n.status || "open") === "completed" && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" data-testid={`handover-note-check-${n.id}`} />}
          <span className={`px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest ${st.cls}`} data-testid={`handover-note-status-${n.id}`}>{st.label}</span>
          <span className="font-mono text-[10px] text-gray-500">{n.by_name} · {fmtWhen(n.created_at)}</span>
          {(user.role === "admin" || n.by_id === user.id) && (
            <button className="ml-auto p-0.5 rounded text-gray-300 hover:text-rose-600" onClick={() => deleteNote(n)} data-testid={`handover-note-del-${n.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <p className={`text-sm mt-1 whitespace-pre-wrap ${(n.status || "open") === "completed" ? "text-gray-500" : "text-gray-700"}`}>{n.text}</p>
        {(n.linked_wo_tail || n.linked_task_name || n.ack_by_name) && (
          <div className="text-[10px] text-gray-500 mt-1 font-mono">
            {n.linked_wo_tail && <span>→ open job on {n.linked_wo_tail}</span>}
            {n.linked_task_name && <span>→ task for {n.linked_task_name}</span>}
            {n.ack_by_name && !n.linked_wo_tail && !n.linked_task_name && <span>Ack by {n.ack_by_name} · {fmtWhen(n.ack_at)}</span>}
          </div>
        )}
        {(n.status || "open") !== "completed" && (
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {(n.status || "open") === "open" && (
              <button className="inline-flex items-center gap-1 text-[11px] text-blue-700 hover:underline" onClick={() => ackNote(n)} data-testid={`handover-note-ack-${n.id}`}>
                <Eye className="w-3 h-3" /> Acknowledge
              </button>
            )}
            {linkable && (
              <>
                <button className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:underline" onClick={() => setPushJob({ note: n, tail: "", priority: "Medium" })} data-testid={`handover-note-pushjob-${n.id}`}>
                  <Wrench className="w-3 h-3" /> Push as open job
                </button>
                <button className="inline-flex items-center gap-1 text-[11px] text-violet-700 hover:underline" onClick={() => setAssignTask({ note: n, assignee_id: "" })} data-testid={`handover-note-assigntask-${n.id}`}>
                  <UserPlus className="w-3 h-3" /> Assign as task
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="handovers-page">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
          <ArrowLeftRight className="w-6 h-6 text-summit-primary" /> Handovers
        </h1>
        <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Completed work · last {summary?.days || 2} days · by base</span>
        <div className="ml-auto flex items-center gap-2">
          <select className="h-8 px-2 rounded-md border border-summit-hairline bg-white text-xs" value={viewBase} onChange={(e) => setViewBase(e.target.value)} data-testid="handover-note-filter">
            <option value="">All bases</option>
            {cities.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
          </select>
          <Button size="sm" variant="outline" className="h-8" onClick={load} data-testid="handovers-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Note composer */}
      <div className="mt-5 border border-summit-hairline rounded-lg bg-white p-4" data-testid="handovers-notes-half">
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5 mb-3">
          <StickyNote className="w-3.5 h-3.5 text-amber-600" /> Add a handover note for the next shift
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-gray-500">Base *</Label>
            <select className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm" value={noteBase} onChange={(e) => setNoteBase(e.target.value)} data-testid="handover-note-base">
              <option value="">Pick a base…</option>
              {cities.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-gray-500">Note *</Label>
            <Textarea className="mt-1 text-sm" rows={2} placeholder="e.g. C-GASB waiting on brake parts — expected on the morning flight." value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="handover-note-text" />
          </div>
          <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white h-10" onClick={addNote} disabled={busy} data-testid="handover-note-add">
            Add note
          </Button>
        </div>
        <p className="text-[10px] text-gray-400 mt-2">Completed notes clear themselves after 5 days.</p>
      </div>

      {/* Per-base sections — completed work and that base's notes side by side */}
      <div className="mt-6 space-y-6">
        {loading && <p className="text-sm text-gray-400">Loading…</p>}
        {!loading && orderedBases.length === 0 && (
          <p className="text-sm text-gray-400 border border-dashed border-summit-hairline rounded-lg p-6 text-center">Nothing to hand over yet.</p>
        )}
        {orderedBases.map((code) => {
          const acs = baseAircraft(code);
          const withWork = acs.filter((a) => a.jobs.length > 0 || (a.daily_inspections || []).length > 0);
          const noWork = acs.filter((a) => a.jobs.length === 0 && (a.daily_inspections || []).length === 0);
          const totalDone = withWork.reduce((n, a) => n + a.jobs.length + (a.daily_inspections || []).length, 0);
          const baseNotes = notes.filter((n) => n.base === code);
          const isHome = code === userBase;
          return (
            <div key={code} className={`border rounded-lg bg-white overflow-hidden ${isHome ? "border-summit-primary shadow-sm" : "border-summit-hairline"}`} data-testid={`handover-base-${code}`}>
              <div className={`px-4 py-2 flex items-center gap-2 text-white ${isHome ? "bg-summit-primary" : "bg-gray-700"}`}>
                <MapPin className="w-3.5 h-3.5" />
                <span className="font-display font-semibold">{code}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest opacity-80">{baseName(code)}</span>
                {isHome && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/20 font-mono text-[9px] uppercase tracking-widest" data-testid="handover-your-base">
                    <Home className="w-3 h-3" /> Your base
                  </span>
                )}
                <span className="ml-auto font-mono text-[10px] opacity-80">{totalDone} completed · {baseNotes.length} note{baseNotes.length === 1 ? "" : "s"}</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-summit-hairline">
                {/* Completed work */}
                <div className="p-3 space-y-3" data-testid={`handover-completed-${code}`}>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-gray-400 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Completed work
                  </div>
                  {withWork.length === 0 && <p className="text-xs text-gray-400">No completed work in the last {summary?.days || 2} days.</p>}
                  {withWork.map((a) => (
                    <div key={a.tail} className="border border-summit-hairline rounded-md" data-testid={`handover-ac-${a.tail}`}>
                      <div className="px-3 py-1.5 bg-summit-canvas/60 flex items-center gap-2">
                        <Plane className="w-3.5 h-3.5 text-summit-primary" />
                        <span className="font-mono text-sm font-medium">{a.tail}</span>
                        {a.fleet && <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">{a.fleet}</span>}
                        <button
                          className="p-0.5 rounded text-gray-400 hover:text-summit-primary transition-colors"
                          onClick={() => openArchive(a.tail)}
                          title="Archived completed jobs (2–30 days ago)"
                          data-testid={`handover-archive-btn-${a.tail}`}
                        >
                          <Archive className="w-3.5 h-3.5" />
                        </button>
                        <span className={`ml-auto px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest ${STATUS_CLS[a.status] || STATUS_CLS.normal}`}>{a.status === "normal" ? "Serviceable" : a.status.replace("_", " ")}</span>
                      </div>
                      <div className="divide-y divide-summit-hairline">
                        {(a.daily_inspections || []).map((di) => (
                          <div key={di.id} className="px-3 py-2 text-xs flex items-start gap-2" data-testid={`handover-di-${di.id}`}>
                            <ClipboardCheck className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                            <div>
                              <span className="font-medium text-emerald-800">Daily Inspection completed</span>
                              <span className="text-[10px] text-gray-500"> · {di.form_number}</span>
                              <div className="text-[10px] text-gray-500 mt-0.5">{di.completed_by_name} · {fmtWhen(di.completed_at)}</div>
                            </div>
                          </div>
                        ))}
                        {a.jobs.map((j) => (
                          <div key={j.id} className="px-3 py-2 text-xs" data-testid={`handover-job-${j.id}`}>
                            <div className="whitespace-pre-wrap font-medium">{j.title}</div>
                            <div className="text-[10px] text-gray-500 mt-0.5">
                              {j.completed_by_name} · {fmtWhen(j.completed_at)}
                              {j.labor_hours ? <span className="font-mono text-sky-700"> · {j.labor_hours} hrs</span> : null}
                              {j.affected_area ? ` · ${j.affected_area}` : ""}
                            </div>
                            {j.completion_notes && <div className="text-[11px] text-gray-600 mt-0.5 whitespace-pre-wrap">{j.completion_notes}</div>}
                            {j.parts && <div className="text-[10px] text-gray-500 mt-0.5">Parts: {j.parts}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {noWork.length > 0 && (
                    <p className="text-[10px] text-gray-400 font-mono" data-testid={`handover-nojobs-${code}`}>
                      No recent completions:{" "}
                      {noWork.map((a, idx) => (
                        <React.Fragment key={a.tail}>
                          <button
                            className="hover:text-summit-primary hover:underline"
                            onClick={() => openArchive(a.tail)}
                            title="View archived completed jobs"
                            data-testid={`handover-archive-link-${a.tail}`}
                          >
                            {a.tail}
                          </button>
                          {idx < noWork.length - 1 ? ", " : ""}
                        </React.Fragment>
                      ))}
                    </p>
                  )}
                </div>
                {/* Notes for this base */}
                <div className="p-3 space-y-2" data-testid={`handover-notes-base-${code}`}>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-gray-400 flex items-center gap-1">
                    <StickyNote className="w-3 h-3 text-amber-600" /> Handover notes
                  </div>
                  {baseNotes.length === 0 && <p className="text-xs text-gray-400">No handover notes for {code}.</p>}
                  {baseNotes.map(renderNote)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Push note as open job */}
      <Dialog open={!!pushJob} onOpenChange={(o) => { if (!o) setPushJob(null); }}>
        <DialogContent className="max-w-md" data-testid="handover-pushjob-form">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Wrench className="w-4 h-4" /> Push as open job</DialogTitle>
            <DialogDescription className="whitespace-pre-wrap">{pushJob?.note?.text?.slice(0, 140)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-500">Aircraft *</Label>
              <select className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm" value={pushJob?.tail || ""} onChange={(e) => setPushJob((p) => ({ ...p, tail: e.target.value }))} data-testid="handover-pushjob-tail">
                <option value="">Pick an aircraft…</option>
                {allTails.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-500">Priority</Label>
              <select className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm" value={pushJob?.priority || "Medium"} onChange={(e) => setPushJob((p) => ({ ...p, priority: e.target.value }))} data-testid="handover-pushjob-priority">
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" disabled={busy} onClick={doPushJob} data-testid="handover-pushjob-save">Create open job</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign note as task */}
      <Dialog open={!!assignTask} onOpenChange={(o) => { if (!o) setAssignTask(null); }}>
        <DialogContent className="max-w-md" data-testid="handover-assigntask-form">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><UserPlus className="w-4 h-4" /> Assign as task</DialogTitle>
            <DialogDescription className="whitespace-pre-wrap">{assignTask?.note?.text?.slice(0, 140)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <EmployeeSearchSelect
              options={members.map((m) => ({ id: m.id, label: m.name, sublabel: m.job_title }))}
              value={assignTask?.assignee_id || ""}
              onChange={(v) => setAssignTask((p) => ({ ...p, assignee_id: v }))}
              triggerPlaceholder="Search & pick a person"
              testId="handover-assigntask-select"
            />
            <p className="text-[10px] text-gray-500">The note lands on their Tasks page — when they mark it Complete, the note gets its green check.</p>
            <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" disabled={busy} onClick={doAssignTask} data-testid="handover-assigntask-save">Assign task</Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Archived completed jobs */}
      <Dialog open={!!archive} onOpenChange={(o) => { if (!o) setArchive(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="handover-archive-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Archive className="w-4 h-4" /> {archive?.tail} — archived completed jobs
            </DialogTitle>
            <DialogDescription>Completed 2–30 days ago.</DialogDescription>
          </DialogHeader>
          {archive?.loading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : (
            <div className="space-y-1.5">
              {(archive?.daily_inspections || []).length === 0 && (archive?.jobs || []).length === 0 && (
                <p className="text-sm text-gray-400" data-testid="handover-archive-empty">No archived completions in the last 30 days.</p>
              )}
              {(archive?.daily_inspections || []).map((di) => (
                <div key={di.id} className="border border-summit-hairline rounded-md px-3 py-2 text-xs flex items-start gap-2" data-testid={`handover-archive-di-${di.id}`}>
                  <ClipboardCheck className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                  <div>
                    <span className="font-medium text-emerald-800">Daily Inspection completed</span>
                    <span className="text-[10px] text-gray-500"> · {di.form_number}</span>
                    <div className="text-[10px] text-gray-500 mt-0.5">{di.completed_by_name} · {fmtWhen(di.completed_at)}</div>
                  </div>
                </div>
              ))}
              {(archive?.jobs || []).map((j) => (
                <div key={j.id} className="border border-summit-hairline rounded-md px-3 py-2 text-xs" data-testid={`handover-archive-job-${j.id}`}>
                  <div className="whitespace-pre-wrap font-medium">{j.title}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">
                    {j.completed_by_name} · {fmtWhen(j.completed_at)}
                    {j.labor_hours ? <span className="font-mono text-sky-700"> · {j.labor_hours} hrs</span> : null}
                    {j.affected_area ? ` · ${j.affected_area}` : ""}
                  </div>
                  {j.completion_notes && <div className="text-[11px] text-gray-600 mt-0.5 whitespace-pre-wrap">{j.completion_notes}</div>}
                  {j.parts && <div className="text-[10px] text-gray-500 mt-0.5">Parts: {j.parts}</div>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
